import { MathHelper } from '@core/helpers/math.helper';
import { SortHelper } from '@core/helpers/sort.helper';
import { ExpenseBillingMethodDto } from '@interfaces/ExpenseBillingMethodDto';
import { ExpenseLine } from '@interfaces/ExpenseLine';
import { InternalFeeBillingMethodDto } from '@interfaces/InternalFeeBillingMethodDto';
import { InternalFeeLine } from '@interfaces/InternalFeeLine';
import { LaborBillingMethodDto } from '@interfaces/LaborBillingMethodDto';

export class BillingMethodService {
  // Get Billing Methods
  public static getLaborBillingMethod(
    laborBillingMethods: LaborBillingMethodDto[],
    classCode: string,
    startingDate: Date,
    categoryCode?: string
  ): LaborBillingMethodDto {
    let laborBillingMethod: LaborBillingMethodDto = undefined;
    let currentLaborBillingMethods: LaborBillingMethodDto[] = [];
    // filter labor billing methods by ClasseID
    const laborBillingMethodsByClass = laborBillingMethods.filter(fm => fm.classId === classCode);

    if (laborBillingMethodsByClass.length > 0) {
      // filter labor billing methods by CostCategorie
      let laborBillingMethodsByCat = categoryCode
        ? laborBillingMethodsByClass.filter(fm => fm.costCategory === categoryCode)
        : laborBillingMethodsByClass.filter(fm => fm.costCategory === '');
      if (categoryCode) {
        laborBillingMethodsByCat = laborBillingMethodsByClass.filter(fm => fm.costCategory === categoryCode);

        if (laborBillingMethodsByCat.length === 0) {
          laborBillingMethodsByCat = laborBillingMethodsByClass.filter(fm => fm.costCategory === '');
        }
      }

      if (laborBillingMethodsByCat.length > 0) {
        currentLaborBillingMethods = laborBillingMethodsByCat;
      }
    }

    // get labor billing method with closest date
    if (currentLaborBillingMethods.length > 0) {
      const laborBillingMethodsDateDesc = SortHelper.sortArrayByProperty(
        currentLaborBillingMethods,
        o => o.startingDate,
        true
      );

      for (const f of laborBillingMethodsDateDesc) {
        if (new Date(f.startingDate) <= startingDate) {
          laborBillingMethod = f;
          break;
        }
      }
    }

    return laborBillingMethod;
  }

  public static getExpenseBillingMethod(
    expenseBillingMethods: ExpenseBillingMethodDto[],
    expenseBillingMethodCode?: string,
    startingDate?: Date,
    categoryCode = ''
  ): ExpenseBillingMethodDto | undefined {
    if (!startingDate || !expenseBillingMethodCode) return undefined;

    const descDateMethods = SortHelper.sortArrayByProperty(expenseBillingMethods, o => o.startingDate, true);
    const foundBillingMethod = descDateMethods.find(
      e =>
        e.expenseBillingMethodCode === expenseBillingMethodCode &&
        e.costCategory === categoryCode &&
        new Date(e.startingDate) <= startingDate
    );

    return foundBillingMethod;
  }

  public static getInternalFeeBillingMethod(
    internalFeeBillingMethods: InternalFeeBillingMethodDto[],
    internalFeeBillingMethodCode?: string,
    startingDate?: Date,
    categoryCode = '',
    resourceCode = ''
  ): InternalFeeBillingMethodDto | undefined {
    if (!startingDate || !internalFeeBillingMethodCode) return undefined;

    const descDateMethods = SortHelper.sortArrayByProperty(internalFeeBillingMethods, o => o.startingDate, true);
    const foundBillingMethod = descDateMethods.find(
      e =>
        e.internalFeeBillingMethodCode === internalFeeBillingMethodCode &&
        e.costCategoryCode === categoryCode &&
        e.resourceCode === resourceCode &&
        new Date(e.startingDate) <= startingDate
    );

    return foundBillingMethod;
  }

  // Cost rates from billing methods
  public static getExpenseCostRate(expenseLine?: ExpenseLine, expenseBillingMethod?: ExpenseBillingMethodDto) {
    let rate = expenseLine?.unitCost ?? 0;

    if (!expenseBillingMethod) return MathHelper.round2Decimals(rate);

    //unitaire
    if (expenseBillingMethod.expenseType === 1) {
      rate = expenseBillingMethod.paidRate === 1 ? expenseBillingMethod.costRate : expenseBillingMethod.unitRate;
    } else if (expenseBillingMethod.expenseType === 2) {
      rate = expenseLine?.unitCost ?? 0;
    }

    return MathHelper.round2Decimals(rate);
  }

  public static getInternalFeeCostRate(
    internalFeeLine: InternalFeeLine,
    internalFeeBillingMethod?: InternalFeeBillingMethodDto
  ) {
    const internalResourceCostRate = internalFeeLine.internalResourceEstimate.internalResource.rate;

    if (!internalFeeBillingMethod) return internalResourceCostRate;

    const rate = internalFeeBillingMethod.paidRate === 1 ? internalFeeBillingMethod.costRate : internalResourceCostRate;

    return MathHelper.round2Decimals(rate);
  }

  // Billable rates from billing methods
  public static getLaborBillableRate(
    costRate: number,
    estimateRate: number,
    laborBillingMethod?: LaborBillingMethodDto
  ) {
    let rate = 0;

    if (!laborBillingMethod) return rate;

    switch (laborBillingMethod.markupType) {
      case 1: {
        rate =
          laborBillingMethod.regularRate *
          (1 + laborBillingMethod.prBurdenRate / 1000000) *
          Math.min(costRate, laborBillingMethod.maxRate);
        break;
      }
      case 2: {
        rate = laborBillingMethod.regularRate;
        break;
      }
      case 3: {
        rate = laborBillingMethod.regularRate * estimateRate;
        break;
      }
      case 4: {
        rate = laborBillingMethod.regularRate + costRate;
        break;
      }
      case 5: {
        rate = laborBillingMethod.regularRate * costRate;
        break;
      }
      default: {
        rate = 0;
        break;
      }
    }

    return MathHelper.round2Decimals(rate);
  }

  public static getExpenseBillableRate(costRate: number, expenseBillingMethod?: ExpenseBillingMethodDto) {
    if (!expenseBillingMethod) return 0;

    const rate =
      expenseBillingMethod?.type === 1
        ? costRate * expenseBillingMethod.billableRate
        : expenseBillingMethod.billableRate;

    return MathHelper.round2Decimals(rate);
  }

  public static getInternalFeeBillableRate(costRate: number, internalFeeBillingMethod?: InternalFeeBillingMethodDto) {
    if (!internalFeeBillingMethod) return 0;

    const rate =
      internalFeeBillingMethod.type === 1
        ? costRate * internalFeeBillingMethod.billableRate
        : internalFeeBillingMethod.billableRate;

    return MathHelper.round2Decimals(rate);
  }
}
